import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';
import Badge from '../../../../components/base/Badge';

echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

interface PromptInjectionPIRSChartProps {
  overallPIRS: number;
  overallRisk: string;
  overallVerdict: string;
  title: string;
  description: string;
}

const PromptInjectionPIRSChart: React.FC<PromptInjectionPIRSChartProps> = ({
  overallPIRS,
  overallRisk,
  overallVerdict,
  title,
  description
}) => {
  const { getThemeColor } = useAppContext();

  // Ensure we have valid colors with fallbacks
  const dangerColor = getThemeColor('danger') || '#dc3545';
  const warningColor = getThemeColor('warning') || '#ffc107';
  const successColor = getThemeColor('success') || '#198754';
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  // Determine color based on risk level
  const getRiskColor = () => {
    switch (overallRisk.toLowerCase()) {
      case 'high':
        return dangerColor;
      case 'medium':
        return warningColor;
      case 'low':
        return successColor;
      default:
        return primaryColor;
    }
  };

  const getVerdictBadgeColor = () => {
    switch (overallVerdict.toUpperCase()) {
      case 'BLOCK':
        return 'danger';
      case 'REVIEW':
        return 'warning';
      case 'APPROVE':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const options = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        return `<strong>Overall PIRS:</strong> ${param.value.toFixed(
          2
        )}%<br/><strong>Risk:</strong> ${overallRisk}<br/><strong>Verdict:</strong> ${overallVerdict}`;
      }
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      splitLine: {
        show: true,
        lineStyle: { color: borderColor, type: 'dashed' }
      }
    },
    yAxis: {
      type: 'category',
      data: [''],
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { show: false }
    },
    series: [
      {
        type: 'bar',
        data: [
          {
            value: overallPIRS,
            itemStyle: {
              color: getRiskColor(),
              borderRadius: [0, 5, 5, 0]
            }
          }
        ],
        barWidth: '30%',
        label: {
          show: true,
          position: 'right',
          formatter: `${overallPIRS.toFixed(2)}%`,
          color: lightTextEmphasis,
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      {
        type: 'bar',
        data: [
          {
            value: 100 - overallPIRS,
            itemStyle: {
              color: borderColor + '40',
              borderRadius: [5, 0, 0, 5]
            }
          }
        ],
        barWidth: '30%',
        barGap: '-100%',
        silent: true
      }
    ],
    grid: { left: '5%', right: '15%', bottom: '10%', top: '10%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">
              {title}
              <Badge bg={getVerdictBadgeColor()} variant="phoenix" pill className="ms-2">
                {overallVerdict}
              </Badge>
            </h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '200px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default PromptInjectionPIRSChart;
